import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    getBusinessById,
    getEmployeeByBusiness,
} from "../../services/firestoreService";
import BusinessLayoutV2 from "../../components/BusinessLayoutV2";
import { useAuth } from "../../context/AuthProvider";
import {createEmployee} from "../../services/onboardingService";
import {getEmployeeReadinessIssue} from "../../lib/onboarding";

export default function BusinessEmployeesV2() {
    const { userData } = useAuth();
    const navigate = useNavigate();

    const [business, setBusiness] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [newEmployeeName, setNewEmployeeName] = useState("");
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState("");

    const visibleEmployees = (employeeData) => Array.isArray(employeeData) ? employeeData : [];

    useEffect(() => {
        const fetchData = async () => {
            if (!userData?.businessID) return;
            setIsLoading(true);

            const [businessData, employeeData] = await Promise.all([
                getBusinessById(userData.businessID),
                getEmployeeByBusiness(userData.businessID),
            ]);

            setBusiness(businessData || null);
            setEmployees(visibleEmployees(employeeData));
            setIsLoading(false);
        };
        fetchData();
    }, [userData]);

    const handleAddEmployee = async (event) => {
        event.preventDefault();
        if (!newEmployeeName.trim()) return;
        setCreating(true);
        setCreateError("");
        try {
            const result = await createEmployee(userData.businessID, newEmployeeName.trim());
            setIsModalOpen(false);
            setNewEmployeeName("");
            navigate(`/business/employee/${result.employeeID}/information`);
        } catch (error) {
            setCreateError(error.message);
        } finally {
            setCreating(false);
        }
    };

    return (
        <BusinessLayoutV2>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&display=swap');

                .biz-employees-v2 * { box-sizing: border-box; margin: 0; padding: 0; }
                .biz-employees-v2 {
                    --surface: #ffffff;
                    --surface-raised: #f0f0f5;
                    --border: #e8e8ee;
                    --text-primary: #1a1a2e;
                    --text-secondary: #6b6b82;
                    --text-muted: #9b9bb0;
                    --accent: #0d7c5f;
                    --accent-light: #e8f5f0;
                    --red-light: #fef2f2;
                    --font: 'DM Sans', -apple-system, sans-serif;
                    font-family: var(--font);
                    color: var(--text-primary);
                    width: 100%;
                    padding: 32px 0 80px;
                }
                .biz-employees-v2 .header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 24px;
                    gap: 12px;
                    flex-wrap: wrap;
                }
                .biz-employees-v2 .title {
                    font-size: 28px;
                    font-weight: 700;
                    letter-spacing: -0.5px;
                }
                .biz-employees-v2 .subtitle {
                    margin-top: 4px;
                    font-size: 14px;
                    color: var(--text-secondary);
                }
                .biz-employees-v2 .add-employee-button {
                    min-height: 42px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    flex-shrink: 0;
                    padding: 10px 16px;
                    border: 1px solid var(--accent);
                    border-radius: 10px;
                    background: var(--accent);
                    color: #ffffff;
                    font-family: var(--font);
                    font-size: 13px;
                    font-weight: 600;
                    line-height: 1.3;
                    cursor: pointer;
                    box-shadow: 0 2px 5px rgba(13, 124, 95, 0.12);
                    transition: background-color 150ms ease, border-color 150ms ease, transform 150ms ease;
                }
                .biz-employees-v2 .add-employee-button:hover { border-color: #09694f; background: #09694f; }
                .biz-employees-v2 .add-employee-button:active { transform: translateY(1px); }
                .biz-employees-v2 .add-employee-button svg { width: 15px; height: 15px; }
                .biz-employees-v2 .list-shell {
                    background: var(--surface);
                    border: 1px solid var(--border);
                    border-radius: 18px;
                    overflow: hidden;
                }
                .biz-employees-v2 .row {
                    padding: 16px 18px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 14px;
                    border-bottom: 1px solid var(--border);
                }
                .biz-employees-v2 .row:last-child { border-bottom: none; }
                .biz-employees-v2 .left {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    min-width: 0;
                }
                .biz-employees-v2 .avatar {
                    width: 44px;
                    height: 44px;
                    border-radius: 9999px;
                    border: 1px solid var(--border);
                    object-fit: cover;
                    flex-shrink: 0;
                }
                .biz-employees-v2 .name {
                    font-size: 15px;
                    font-weight: 600;
                }
                .biz-employees-v2 .email {
                    font-size: 13px;
                    color: var(--text-secondary);
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                .biz-employees-v2 .right {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    flex-shrink: 0;
                }
                .biz-employees-v2 .deactivated {
                    font-size: 11px;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 4px 10px;
                    border-radius: 6px;
                    background: var(--red-light);
                    color: #b91c1c;
                    border: 1px solid #fecaca;
                }
                .biz-employees-v2 .details-link {
                    font-size: 13px;
                    font-weight: 600;
                    color: var(--accent);
                    text-decoration: none;
                    border: 1px solid var(--accent);
                    border-radius: 8px;
                    padding: 7px 10px;
                }
                .biz-employees-v2 .details-link:hover { background: var(--accent-light); }
                .biz-employees-v2 .empty {
                    background: var(--surface);
                    border: 1px solid var(--border);
                    border-radius: 14px;
                    padding: 36px 20px;
                    color: var(--text-secondary);
                    text-align: center;
                    font-size: 14px;
                }
                .biz-employees-v2 .empty-icon {
                    width: 42px;
                    height: 42px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 12px;
                    border-radius: 12px;
                    background: var(--accent-light);
                    color: var(--accent);
                }
                .biz-employees-v2 .empty-title { color: var(--text-primary); font-size: 15px; font-weight: 600; line-height: 1.4; }
                .biz-employees-v2 .empty-copy { margin-top: 4px; color: var(--text-secondary); font-size: 13px; line-height: 1.45; }
                .biz-employees-v2 .loading-wrap {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    min-height: 140px;
                    background: var(--surface);
                    border: 1px solid var(--border);
                    border-radius: 14px;
                }
                .biz-employees-v2 .spinner {
                    width: 28px;
                    height: 28px;
                    border: 3px solid #dbe8e2;
                    border-top-color: var(--accent);
                    border-radius: 9999px;
                    animation: spin 0.8s linear infinite;
                }
                .biz-employees-v2 .employee-modal-overlay {
                    position: fixed;
                    inset: 0;
                    z-index: 50;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 20px;
                    background: rgba(20, 20, 33, 0.54);
                    backdrop-filter: blur(2px);
                }
                .biz-employees-v2 .employee-modal {
                    position: relative;
                    width: min(100%, 440px);
                    padding: 28px;
                    border: 1px solid var(--border);
                    border-radius: 18px;
                    background: var(--surface);
                    box-shadow: 0 24px 70px rgba(20, 20, 33, 0.22);
                }
                .biz-employees-v2 .employee-modal-heading { padding-right: 36px; }
                .biz-employees-v2 .employee-modal-title { color: var(--text-primary); font-size: 22px; font-weight: 700; line-height: 1.25; letter-spacing: -0.25px; }
                .biz-employees-v2 .employee-modal-description { margin-top: 7px; color: var(--text-secondary); font-size: 13px; line-height: 1.5; }
                .biz-employees-v2 .employee-modal-close {
                    position: absolute;
                    top: 18px;
                    right: 18px;
                    width: 34px;
                    height: 34px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border: none;
                    border-radius: 9px;
                    background: transparent;
                    color: var(--text-secondary);
                    font-family: var(--font);
                    font-size: 23px;
                    font-weight: 300;
                    line-height: 1;
                    cursor: pointer;
                }
                .biz-employees-v2 .employee-modal-close:hover { background: var(--surface-raised); color: var(--text-primary); }
                .biz-employees-v2 .employee-field { display: block; margin-top: 22px; }
                .biz-employees-v2 .employee-field-label { display: block; margin-bottom: 7px; color: var(--text-primary); font-size: 13px; font-weight: 600; line-height: 1.4; }
                .biz-employees-v2 .employee-name-input {
                    width: 100%;
                    height: 46px;
                    padding: 0 13px;
                    border: 1px solid #d4d4dd;
                    border-radius: 10px;
                    outline: none;
                    background: #ffffff;
                    color: var(--text-primary);
                    font-family: var(--font);
                    font-size: 14px;
                    line-height: 1.4;
                    transition: border-color 150ms ease, box-shadow 150ms ease;
                }
                .biz-employees-v2 .employee-name-input::placeholder { color: var(--text-muted); }
                .biz-employees-v2 .employee-name-input:focus { border-color: var(--accent); box-shadow: 0 0 0 3px rgba(13, 124, 95, 0.12); }
                .biz-employees-v2 .employee-field-hint { margin-top: 7px; color: var(--text-muted); font-size: 12px; line-height: 1.4; }
                .biz-employees-v2 .employee-create-error {
                    margin-top: 14px;
                    padding: 10px 12px;
                    border-radius: 9px;
                    background: var(--red-light);
                    color: #b91c1c;
                    font-size: 12px;
                    line-height: 1.4;
                }
                .biz-employees-v2 .employee-modal-actions { display: flex; align-items: center; justify-content: flex-end; gap: 10px; margin-top: 24px; }
                .biz-employees-v2 .employee-modal-button {
                    min-height: 42px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    padding: 10px 16px;
                    border-radius: 10px;
                    font-family: var(--font);
                    font-size: 13px;
                    font-weight: 600;
                    line-height: 1.3;
                    cursor: pointer;
                    transition: background-color 150ms ease, border-color 150ms ease;
                }
                .biz-employees-v2 .employee-modal-button.secondary { border: 1px solid var(--border); background: #ffffff; color: var(--text-secondary); }
                .biz-employees-v2 .employee-modal-button.secondary:hover:not(:disabled) { border-color: var(--border-bright); background: var(--surface-raised); color: var(--text-primary); }
                .biz-employees-v2 .employee-modal-button.primary { border: 1px solid var(--accent); background: var(--accent); color: #ffffff; }
                .biz-employees-v2 .employee-modal-button.primary:hover:not(:disabled) { border-color: #09694f; background: #09694f; }
                .biz-employees-v2 .employee-modal-button:disabled { cursor: not-allowed; opacity: 0.45; }
                @keyframes spin { to { transform: rotate(360deg); } }
                @media (max-width: 700px) {
                    .biz-employees-v2 .row { align-items: flex-start; }
                    .biz-employees-v2 .right { flex-direction: column; align-items: flex-end; }
                    .biz-employees-v2 .email { white-space: normal; }
                }
                @media (max-width: 480px) {
                    .biz-employees-v2 .header { align-items: stretch; }
                    .biz-employees-v2 .add-employee-button { width: 100%; }
                    .biz-employees-v2 .employee-modal { padding: 24px 20px 20px; }
                    .biz-employees-v2 .employee-modal-actions { align-items: stretch; flex-direction: column-reverse; }
                    .biz-employees-v2 .employee-modal-button { width: 100%; }
                }
            `}</style>

            <div className="biz-employees-v2">
                <header className="header">
                    <div>
                        <h1 className="title">Employees</h1>
                        <p className="subtitle">{business?.displayName || business?.name || userData?.businessID || "Business"} · Team directory</p>
                    </div>
                    <button type="button" onClick={() => { setCreateError(""); setIsModalOpen(true); }} className="add-employee-button">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M12 5v14M5 12h14"/>
                        </svg>
                        Add employee
                    </button>
                </header>

                {isLoading ? (
                    <div className="loading-wrap">
                        <div className="spinner" />
                    </div>
                ) : employees.length === 0 ? (
                    <div className="empty">
                        <span className="empty-icon" aria-hidden="true">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M19 8v6M22 11h-6"/>
                            </svg>
                        </span>
                        <p className="empty-title">No employees yet</p>
                        <p className="empty-copy">Add your first employee to begin payout setup.</p>
                    </div>
                ) : (
                    <div className="list-shell">
                        {employees.map((employee) => {
                            const isDeactivated = employee.accountStatus === "deactivated";
                            const readinessIssue = isDeactivated
                                ? "Employee is deactivated"
                                : employee.accountStatus === "readonly"
                                    ? "Tipping is paused"
                                    : getEmployeeReadinessIssue(employee, business);
                            return (
                                <article key={employee.id} className="row">
                                    <div className="left">
                                        <img
                                            src={employee.imgUrl || "https://i.pravatar.cc/80"}
                                            alt={employee.displayName || "Employee"}
                                            className="avatar"
                                            style={isDeactivated ? {opacity: 0.5} : undefined}
                                        />
                                        <div>
                                            <p className="name">{employee.displayName || `${employee.firstName || ""} ${employee.lastName || ""}`.trim() || "Employee"}</p>
                                            <p className="email">{readinessIssue || (employee.email ? `Ready · ${employee.email}` : "Payout-ready")}</p>
                                        </div>
                                    </div>

                                    <div className="right">
                                        {isDeactivated && <span className="deactivated">Deactivated</span>}
                                        <Link to={`/business/employee/${employee.id}`} className="details-link">
                                            {readinessIssue ? "Complete setup" : "View details"}
                                        </Link>
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                )}

                {isModalOpen && (
                    <div className="employee-modal-overlay" onMouseDown={(event) => {
                        if (event.target === event.currentTarget && !creating) setIsModalOpen(false);
                    }}>
                        <form onSubmit={handleAddEmployee} className="employee-modal" role="dialog" aria-modal="true" aria-labelledby="add-employee-title">
                            <button type="button" className="employee-modal-close" aria-label="Close" disabled={creating} onClick={() => setIsModalOpen(false)}>×</button>
                            <div className="employee-modal-heading">
                                <h2 id="add-employee-title" className="employee-modal-title">Add employee</h2>
                                <p className="employee-modal-description">Create a profile now, then add the employee’s payout details.</p>
                            </div>
                            <label className="employee-field">
                                <span className="employee-field-label">Display name</span>
                                <input autoFocus type="text" value={newEmployeeName} onChange={(event) => setNewEmployeeName(event.target.value)} placeholder="For example, Mari" maxLength={100} required className="employee-name-input" />
                                <span className="employee-field-hint">This name will be shown on their Tiply page.</span>
                            </label>
                            {createError && <p className="employee-create-error" role="alert">{createError}</p>}
                            <div className="employee-modal-actions">
                                <button type="button" disabled={creating} className="employee-modal-button secondary" onClick={() => setIsModalOpen(false)}>
                                    Cancel
                                </button>
                                <button type="submit" disabled={creating || !newEmployeeName.trim()} className="employee-modal-button primary">
                                    {creating ? "Creating…" : "Create draft"}
                                </button>
                            </div>
                        </form>
                    </div>
                )}
            </div>
        </BusinessLayoutV2>
    );
}
